import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Reveal from "./Reveal";
import Tilt3D from "./Tilt3D";


const CHAPTERS = [
  {
    num: "01",
    title: "The Name",
    body: "Every great house begins with a founder. Dewan Ali built ATW — Around The World — on a single promise: that his name would only ever be stamped in gold on things worth keeping.",
    image: "/brand/packaging.jpg",
  },
  {
    num: "02",
    title: "The Craft",
    body: "Trousers cut with a razor crease. Perfumes blended like signatures. Watches that keep more than time. Nothing ships until it deserves the name.",
    image: "/brand/looks/tailored-wool-v4.jpg",
    aspect: "aspect-[3/4]",
    pos: "50% 8%",
  },
  {
    num: "03",
    title: "The World",
    body: "A creation of Around The World Ltd — from our atelier to your doorstep, wherever on earth you happen to stand.",
    image: "/brand/atw-voyage.jpg",
  },
];

function ParallaxImage({ src, alt, className, pos = "center" }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);
  return (
    <Tilt3D max={8} className={`overflow-hidden ${className}`}>
      <div ref={ref} className="h-full w-full">
        <motion.img style={{ y, objectPosition: pos }} src={src} alt={alt} loading="lazy" className="h-full w-full object-cover scale-[1.2]" />
      </div>
    </Tilt3D>
  );
}

function Chapter({ c, i }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const numX = useTransform(scrollYProgress, [0, 1], [i % 2 ? 80 : -80, i % 2 ? -80 : 80]);
  const numOpacity = useTransform(scrollYProgress, [0, 0.5, 1], [0, 0.18, 0]);

  return (
    <div ref={ref} className="relative">
      <motion.span
        aria-hidden="true"
        style={{ x: numX, opacity: numOpacity }}
        className={`absolute -top-16 md:-top-28 ${i % 2 ? "right-0" : "left-0"} font-serif italic text-[26vw] md:text-[18vw] leading-none text-gold-light pointer-events-none select-none`}
      >
        {c.num}
      </motion.span>
      <div className={`relative grid md:grid-cols-12 gap-8 md:gap-14 items-center ${i % 2 === 1 ? "md:[direction:rtl]" : ""}`}>
        <Reveal className={`md:col-span-5 ${i % 2 === 1 ? "md:col-start-8" : ""} [direction:ltr]`}>
          <div className="flex items-baseline gap-6">
            <span className="font-serif italic text-5xl md:text-7xl text-gold-light/80 leading-none">{c.num}</span>
            <h3 className="font-serif text-3xl md:text-5xl tracking-tight">{c.title}</h3>
          </div>
          <p className="mt-6 md:mt-8 text-base md:text-lg text-ivory/60 leading-relaxed max-w-md">{c.body}</p>
        </Reveal>
        <Reveal delay={0.15} className={`md:col-span-6 ${i % 2 === 1 ? "md:col-start-1" : "md:col-start-7"} [direction:ltr]`}>
          <ParallaxImage src={c.image} alt={c.title} pos={c.pos} className={`${c.aspect || "aspect-[4/3]"} w-full`} />
        </Reveal>
      </div>
    </div>
  );
}

export default function Manifesto() {
  return (
    <section
      id="manifesto"
      data-testid="manifesto-section"
      className="relative bg-obsidian text-ivory py-20 md:py-28 px-6 lg:px-12 overflow-hidden"
    >
      <div className="relative max-w-[1400px] mx-auto">
        <Reveal>
          <p className="text-[10px] md:text-xs tracking-[0.5em] uppercase text-gold-light mb-6">
            The Manifesto
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight max-w-4xl">
            Luxury is not a price.
            <span className="italic gold-text"> It is a standard.</span>
          </h2>
        </Reveal>

        <div className="mt-16 md:mt-24 space-y-20 md:space-y-28">
          {CHAPTERS.map((c, i) => (
            <Chapter key={c.num} c={c} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
